"use client";

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/shadcn-components/ui/drawer";
import { Button } from "@/shadcn-components/ui/button";
import { ChevronRightIcon, InfoIcon, BarChart3Icon, XIcon } from "lucide-react";
import { ChartIcon, TradeIcon, DeleteIcon } from "@/components/icons";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client";
import { formatWatchlistPrice } from "../watchlist/watchlistQuoteUtils";
import { getSymbolDescription } from "../lib/symbolDescriptions";

interface SymbolActionsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  symbol: string;
  onTrade: () => void;
  onChart: () => void;
  onDetails: () => void;
  onStatistics: () => void;
  onDelete: () => void;
}

interface ActionRow {
  id: string;
  icon: React.ReactNode;
  label: string;
  hint: string;
  action: () => void;
}

export default function SymbolActionsDrawer({
  isOpen,
  onClose,
  symbol,
  onTrade,
  onChart,
  onDetails,
  onStatistics,
  onDelete,
}: SymbolActionsDrawerProps) {
  const entry = useSubscribedSymbolsStore((state) =>
    state.byCurrency.get(symbol),
  );

  const description = getSymbolDescription(symbol, entry?.symbol.description);
  const quote = entry?.quote;

  const handleAction = (action: () => void) => {
    action();
    onClose();
  };

  const rows: ActionRow[] = [
    {
      id: "trade",
      icon: <TradeIcon size={16} />,
      label: "Trade",
      hint: "new order",
      action: onTrade,
    },
    {
      id: "chart",
      icon: <ChartIcon size={16} />,
      label: "Chart",
      hint: "open in chart tab",
      action: onChart,
    },
    {
      id: "details",
      icon: <InfoIcon size={16} />,
      label: "Details",
      hint: "contract spec",
      action: onDetails,
    },
    {
      id: "statistics",
      icon: <BarChart3Icon size={16} />,
      label: "Statistics",
      hint: "session hi/lo",
      action: onStatistics,
    },
  ];

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent>
        <DrawerHeader className="px-4 pt-2 pb-0 text-left">
          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-4 py-2.5">
            <div className="min-w-0">
              <DrawerTitle className="text-base font-semibold text-foreground">
                {symbol}
              </DrawerTitle>
              <DrawerDescription className="truncate text-xs text-muted-foreground">
                {description}
              </DrawerDescription>
            </div>
            {quote && (
              <div className="ml-3 flex-shrink-0 text-right font-mono text-xs tabular-nums leading-5">
                <div className="text-blue-400">
                  {formatWatchlistPrice(quote.bid, symbol)}
                </div>
                <div className="text-red-400">
                  {formatWatchlistPrice(quote.ask, symbol)}
                </div>
              </div>
            )}
          </div>
        </DrawerHeader>

        <div className="px-4 pb-4 pt-3">
          <div className="overflow-hidden rounded-lg border border-border">
            {rows.map((row, index) => (
              <button
                key={row.id}
                type="button"
                onClick={() => handleAction(row.action)}
                className={`flex h-11 w-full items-center gap-3 px-4 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/50 active:bg-muted/60 ${
                  index < rows.length - 1 ? "border-b border-border/60" : ""
                }`}
              >
                <span className="flex w-5 justify-center text-muted-foreground">
                  {row.icon}
                </span>
                <span className="flex-1">{row.label}</span>
                <span className="font-mono text-[10px] text-muted-foreground/70">
                  {row.hint}
                </span>
                <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>

          <div className="mt-2 overflow-hidden rounded-lg border border-red-500/35">
            <button
              type="button"
              onClick={() => handleAction(onDelete)}
              className="flex h-11 w-full items-center gap-3 px-4 text-left text-sm font-medium text-red-500 transition-colors hover:bg-red-500/10 active:bg-red-500/15"
            >
              <span className="flex w-5 justify-center">
                <DeleteIcon size={16} />
              </span>
              <span className="flex-1">Remove from watchlist</span>
            </button>
          </div>

          <div className="mt-3">
            <DrawerClose asChild>
              <Button variant="secondary" className="h-10 w-full text-sm">
                <XIcon className="mr-2 h-3.5 w-3.5" />
                Cancel
              </Button>
            </DrawerClose>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
